/**
 * AI Handoff ページ (AI 振る舞い規約 + AI Copy 全文)
 *
 * AI がこのサイトへ MCP でアクセスできない場合の暫定手段として、DB から生成した
 * Design Guide 全文 (Markdown) をコピーして AI チャットに貼り付けられるようにする。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business AI振る舞い規約 (役割・判定別応答・相談先) の表示・編集と、DB から生成した AI Copy 全文のコピー。
 * @graph-connects api [calls] aiHandoff.get / aiHandoff.update / guide.getMarkdown
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { AiHandoffPolicyInput } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { useBrandScope } from '../components/brand-scope-context.js';
import { copyText } from '../lib/clipboard.js';
import {
  EmptyState,
  Field,
  GhostButton,
  GlassCard,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
} from '../components/ui.js';

/**
 * AI Handoff ページ。
 * @returns ページ要素
 * @graph-connects api [calls] aiHandoff.get / aiHandoff.update / guide.getMarkdown
 */
export function AiHandoffPage(): JSX.Element {
  const auth = useAuth();
  const brandScope = useBrandScope();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const policyQuery = useQuery(orpc.aiHandoff.get.queryOptions({ input: {} }));
  const markdownQuery = useQuery(
    orpc.guide.getMarkdown.queryOptions({
      input: brandScope.scope === '' ? {} : { brand: brandScope.scope },
    }),
  );
  const policy = policyQuery.data ?? null;
  const markdown = markdownQuery.data?.markdown ?? '';

  const [draft, setDraft] = useState<AiHandoffPolicyInput | null>(null);
  const [expanded, setExpanded] = useState(false);

  const updateMutation = useMutation(
    orpc.aiHandoff.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.aiHandoff.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.guide.key() });
        setDraft(null);
        showToast('success', 'AI Handoff ポリシーを更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const behaviors: { label: string; key: keyof AiHandoffPolicyInput['behavior'] }[] = [
    { label: 'NG', key: 'onNg' },
    { label: '注意', key: 'onCaution' },
    { label: 'OK', key: 'onOk' },
  ];

  return (
    <div>
      <SectionHeading
        title="AI Handoff"
        action={
          auth.canEdit && policy ? (
            <GhostButton
              onClick={() => {
                setDraft({
                  role: policy.role,
                  coreRule: policy.coreRule,
                  behavior: { ...policy.behavior },
                  contacts: policy.contacts.map((contact) => ({ ...contact })),
                });
              }}
            >
              編集
            </GhostButton>
          ) : null
        }
      />
      {!policy ? (
        <EmptyState message="AI Handoff ポリシーが未登録です (seed 未実行)" />
      ) : (
        <GlassCard className="mb-8">
          <div className="mb-2 text-micro font-bold uppercase tracking-label text-muted">
            あなたの役割
          </div>
          <div className="mb-1 text-sm font-bold text-foreground">{policy.role}</div>
          <p className="mb-4 text-body text-subtle">{policy.coreRule}</p>
          <ul className="space-y-1.5 text-body text-subtle">
            {behaviors.map((behavior) => (
              <li key={behavior.key}>
                <span className="mr-2 font-bold text-foreground">{behavior.label} →</span>
                {policy.behavior[behavior.key]}
              </li>
            ))}
          </ul>
          {policy.contacts.length > 0 && (
            <div className="mt-4 border-t border-line-light pt-3 text-xs text-muted">
              {policy.contacts.map((contact) => (
                <div key={contact.topic}>
                  {contact.topic} → {contact.owner}
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      )}

      <SectionHeading title="AI Copy" />
      <GlassCard>
        <p className="mb-4 text-body text-subtle">
          AI がこのガイドに MCP でアクセスできない場合の暫定手段。コピーして AI
          チャットに貼り付け、デザイン画像と一緒に送信してください (内容は DB から自動生成)。
        </p>
        <textarea
          readOnly
          value={markdown}
          className={`w-full resize-y rounded-lg border border-line bg-background p-4 font-mono text-caption leading-relaxed text-subtle ${expanded ? 'textarea-expanded' : 'h-64'}`}
        />
        <div className="mt-3 flex gap-2">
          <PrimaryButton
            onClick={() => {
              void copyText(markdown).then((copied) => {
                showToast(
                  copied ? 'success' : 'error',
                  copied ? 'AI Copy をコピーしました' : 'コピーに失敗しました',
                );
              });
            }}
          >
            Copy to clipboard
          </PrimaryButton>
          <GhostButton
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </GhostButton>
        </div>
      </GlassCard>

      <Modal
        open={draft !== null}
        title="AI Handoff ポリシーを編集"
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="役割">
              <input
                className={INPUT_CLASS}
                value={draft.role}
                onChange={(event) => {
                  setDraft({ ...draft, role: event.target.value });
                }}
              />
            </Field>
            <Field label="コアルール">
              <textarea
                className={`${INPUT_CLASS} h-20 resize-y`}
                value={draft.coreRule}
                onChange={(event) => {
                  setDraft({ ...draft, coreRule: event.target.value });
                }}
              />
            </Field>
            {behaviors.map((behavior) => (
              <Field key={behavior.key} label={`${behavior.label} のときの応答`}>
                <textarea
                  className={`${INPUT_CLASS} h-16 resize-y`}
                  value={draft.behavior[behavior.key]}
                  onChange={(event) => {
                    setDraft({
                      ...draft,
                      behavior: { ...draft.behavior, [behavior.key]: event.target.value },
                    });
                  }}
                />
              </Field>
            ))}
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={updateMutation.isPending}
                onClick={() => {
                  updateMutation.mutate(draft);
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
